import {
  Badge,
  Icon,
  Input,
  Select,
  SelectTypes,
  Text,
  UI,
  HStack,
  Stack,
  VStack,
} from '@genial/components';
import { BoxBorder, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { IconButton, IStackProps } from 'native-base';
import React, { useCallback, useMemo } from 'react';
import { useFormContext, Control, Controller } from 'react-hook-form';

import { LateralBar } from '../../../../../../../../components/LateralBar';
import { BeneficiariesFormData } from '../../constants';

interface BeneficiaryFormProps {
  index: number;
  BadgeTitle: string;
  onRemove: () => void;
  showRemoveButton: boolean;
  beneficiariesRelationship: SelectTypes.Option[];
  isLoadingConfig: boolean;
}

interface FormFieldProps {
  label: string;
  children: React.ReactNode;
  zIndex?: IStackProps['zIndex'];
}

const FormField: React.FC<FormFieldProps> = ({ label, children, zIndex }) => (
  <VStack flex={1} space={Spacing['inline.xxxs']} w="full" zIndex={zIndex}>
    <Text variant="semibold" fontSize={Typography['fontsize.md']} lineHeight="distant">
      {label}
    </Text>
    {children}
  </VStack>
);

export const BeneficiaryForm: React.FC<BeneficiaryFormProps> = ({
  index,
  BadgeTitle,
  onRemove,
  showRemoveButton,
  beneficiariesRelationship,
  isLoadingConfig,
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<BeneficiariesFormData>();

  const participationError = useMemo(
    () =>
      errors?.beneficiaries?.[index]?.participation?.message ||
      (Array.isArray(errors?.beneficiaries) && errors.beneficiaries[0]?.message),
    [errors?.beneficiaries, index]
  );

  const handleParticipationChange = useCallback(() => {
    setTimeout(() => {
      trigger('beneficiaries');
    }, 50);
  }, [trigger]);

  const handleInputChange = useCallback(() => {
    setTimeout(() => {
      trigger('beneficiaries');
    }, 200);
  }, [trigger]);

  const renderHeader = () => (
    <HStack justifyContent="space-between" alignItems="center" mb={Spacing['stack.xxxs']}>
      <Badge
        testId="table-content-item-badge"
        backgroundColor="feedback.gen.lightest"
        textColor={'brand.tertiary.dark'}>
        {BadgeTitle}
      </Badge>
      {showRemoveButton && (
        <HStack space={Spacing['inline.nano']} alignItems="center">
          <UI.Text variant="semibold" color="feedback.error.default">
            REMOVER
          </UI.Text>
          <IconButton
            icon={<Icon name="Trash" size="xs" color={Color['feedback.error.default']} />}
            onPress={onRemove}
          />
        </HStack>
      )}
    </HStack>
  );

  const renderNameField = () => (
    <FormField label="Nome completo">
      <UI.Input
        testId="beneficiary-name-input"
        name={`beneficiaries.${index}.name`}
        control={control as unknown as Control}
        autoComplete="off"
        variant="filled"
        hasShadow={false}
        placeholder="Digite o nome completo"
      />
    </FormField>
  );

  const renderBirthDateField = () => (
    <FormField label="Data de nascimento" zIndex={10}>
      <Controller
        control={control}
        name={`beneficiaries.${index}.birthDate`}
        render={({ field: { onChange, value } }) => (
          <UI.DatePicker
            onClear={() => onChange(undefined)}
            onSelectDate={onChange}
            calendarPosition="left"
            placeholder="Selecione a data"
            date={value ? new Date(value) : undefined}
          />
        )}
      />
    </FormField>
  );

  const renderRelationshipField = () => (
    <FormField label="Parentesco">
      <Controller
        control={control}
        name={`beneficiaries.${index}.relationship`}
        render={({ field: { onChange, value } }) => (
          <Select
            testId="beneficiary-relationship-input"
            placeholder="Selecione o grau de parentesco"
            options={beneficiariesRelationship}
            selectedValue={value}
            onValueChange={onChange}
            triggerProps={{ bg: Color.background }}
            maxOptionPerRender={4}
            disabled={isLoadingConfig}
          />
        )}
      />
    </FormField>
  );

  const renderParticipationField = () => (
    <FormField label="Participação">
      <Controller
        control={control}
        name={`beneficiaries.${index}.participation`}
        render={({ field: { onChange, value, name } }) => (
          <Input
            testId="beneficiary-participation-input"
            name={name}
            autoComplete="off"
            variant="filled"
            hasShadow={false}
            placeholder="Digite a % de participação"
            keyboardType="numeric"
            error={participationError}
            helpText={participationError}
            helpTextProps={{ color: 'feedback.error.default' }}
            value={value || ''}
            onChange={(event) => {
              const text = event.nativeEvent.text;
              const maskedValue = Utils.Mask.InputPercentage(text);
              onChange(maskedValue);
              handleInputChange();
            }}
            onBlur={() => {
              const currentValue = value || '';
              const maskedValue = Utils.Mask.InputPercentage(currentValue);
              if (maskedValue !== currentValue) {
                onChange(maskedValue);
              }
              handleParticipationChange();
            }}
          />
        )}
      />
    </FormField>
  );

  return (
    <UI.Shadow level={1} borderRadius={BoxBorder.heavy} p={Spacing['stack.xxxs']}>
      <LateralBar barColor="product.previdencia" />
      {renderHeader()}
      <VStack w={'100%'} space={Spacing['inline.xxxs']}>
        <Stack
          alignItems={'flex-start'}
          space={Spacing['inline.xxxs']}
          direction={Utils.Responsive.PhoneTablet({ tablet: 'row', phone: 'column' })}
          zIndex={10}>
          {renderNameField()}
          {renderBirthDateField()}
        </Stack>
        <Stack
          alignItems={'flex-start'}
          space={Spacing['inline.xxxs']}
          direction={Utils.Responsive.PhoneTablet({ tablet: 'row', phone: 'column' })}>
          {renderRelationshipField()}
          {renderParticipationField()}
        </Stack>
      </VStack>
    </UI.Shadow>
  );
};
